import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getLearningPath } from '../../services/learningPaths';
import { Route, ArrowLeft, Users, Star, Clock, BookOpen, CheckCircle, ChevronRight } from 'lucide-react';

export default function LearningPathDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getLearningPath(id)
            .then(r => setData(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [id]);

    const diffColors = { beginner: 'bg-green-100 text-green-700', intermediate: 'bg-yellow-100 text-yellow-700', advanced: 'bg-red-100 text-red-700' };
    const diffLabels = { beginner: "Boshlang'ich", intermediate: "O'rta", advanced: "Yuqori" };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" /></div>;
    if (!data) return <div className="text-center py-12 text-gray-500">Topilmadi</div>;

    const path = data.path;
    const courses = data.courses || [];

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <Link to="/learning-paths" className="text-blue-600 text-sm font-medium hover:text-blue-700 flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> O'quv yo'llari
            </Link>

            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-8 text-white">
                <div className="flex items-center gap-2 mb-3">
                    <Route className="w-6 h-6" />
                    {path.difficulty && (
                        <span className="text-xs bg-white/20 px-3 py-1 rounded-full">{diffLabels[path.difficulty]}</span>
                    )}
                    <span className="text-xs bg-white/20 px-3 py-1 rounded-full">{courses.length} ta kurs</span>
                </div>
                <h1 className="text-3xl font-bold mb-3">{path.title}</h1>
                <p className="text-teal-100 max-w-2xl">{path.description}</p>
                {path.creator && (
                    <p className="text-sm text-teal-200 mt-3 flex items-center gap-1">
                        <Users className="w-4 h-4" /> {path.creator.name} tomonidan yaratilgan
                    </p>
                )}
            </div>

            {/* Course sequence */}
            <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Kurslar ketma-ketligi</h2>
                {courses.length > 0 ? (
                    <div className="space-y-3">
                        {courses.map((course, idx) => (
                            <div key={course.id} className="relative">
                                {idx < courses.length - 1 && (
                                    <div className="absolute left-6 top-16 w-0.5 h-6 bg-gray-200 dark:bg-gray-700" />
                                )}
                                <Link to={`/courses/${course.id}`}
                                    className="flex items-center gap-4 bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-100 dark:border-gray-700 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-700 transition group">
                                    <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 flex items-center justify-center text-lg font-bold flex-shrink-0">
                                        {idx + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition">{course.title}</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{course.description}</p>
                                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                            {course.teacher && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {course.teacher.name}</span>}
                                            {course.rating > 0 && <span className="flex items-center gap-1 text-yellow-500"><Star className="w-3 h-3" /> {course.rating}</span>}
                                            <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {course.enrollments_count || 0} talaba</span>
                                            {course.estimated_hours && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {course.estimated_hours} soat</span>}
                                        </div>
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition flex-shrink-0" />
                                </Link>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 text-center border border-gray-100 dark:border-gray-700">
                        <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">Bu yo'lda hali kurslar yo'q</p>
                    </div>
                )}
            </div>
        </div>
    );
}
